import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { 
  Package, Plus, Trash2, Pencil, DollarSign, TrendingUp,
  ShoppingCart, Search, BarChart3, Boxes, ArrowUpRight, ArrowDownRight,
  RefreshCw, Minus, ChevronLeft, ChevronRight, Tag, X, Settings, Check
} from "lucide-react";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value);
};

const produtoSchema = z.object({
  nome: z.string().min(1, "Nome obrigatório"),
  descricao: z.string().optional().default(""),
  categoria: z.string().optional().default(""),
  custo: z.coerce.number().min(0, "Valor inválido"),
  venda: z.coerce.number().min(0, "Valor inválido"),
  quantidade: z.coerce.number().int().min(0, "Quantidade inválida"),
  imagem_url: z.string().optional().default(""),
});

// Categorias sugeridas
const CATEGORIAS_SUGERIDAS = [
  "TV Box",
  "Cabos",
  "Controles",
  "Acessórios",
  "Suportes",
  "Adaptadores",
  "Outros"
];

export default function ProdutosPage() {
  const { authAxios } = useAuth();
  const [produtos, setProdutos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingProduto, setEditingProduto] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState("");
  const [categorias, setCategorias] = useState([]);
  const [categoriasDialogOpen, setCategoriasDialogOpen] = useState(false);
  const [novaCategoria, setNovaCategoria] = useState("");
  const [editandoCategoria, setEditandoCategoria] = useState(null);
  const [editandoCategoriaValor, setEditandoCategoriaValor] = useState("");
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const form = useForm({
    resolver: zodResolver(produtoSchema),
    defaultValues: {
      nome: "",
      descricao: "",
      categoria: "",
      custo: 0,
      venda: 0,
      quantidade: 0,
      imagem_url: "",
    },
  });

  const custo = form.watch("custo") || 0;
  const venda = form.watch("venda") || 0;
  const lucroPreview = venda - custo;
  const margemPreview = venda > 0 ? ((lucroPreview / venda) * 100).toFixed(1) : 0;

  const fetchProdutos = useCallback(async () => {
    try {
      const res = await authAxios.get("/produtos");
      setProdutos(res.data);
    } catch (err) {
      toast.error("Erro ao carregar produtos");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await authAxios.get("/produtos/stats");
      setStats(res.data);
    } catch (err) {
      console.error("Erro ao carregar estatísticas");
    }
  }, [authAxios]);

  const fetchCategorias = useCallback(async () => {
    try {
      const res = await authAxios.get("/produtos/categorias");
      setCategorias(res.data);
    } catch (err) {
      console.error("Erro ao carregar categorias");
    }
  }, [authAxios]);

  useEffect(() => {
    fetchProdutos();
    fetchStats();
    fetchCategorias();
  }, [fetchProdutos, fetchStats, fetchCategorias]);

  const handleOpenDialog = (produto = null) => {
    if (produto) {
      setEditingProduto(produto);
      form.reset({
        nome: produto.nome,
        descricao: produto.descricao || "",
        categoria: produto.categoria || "",
        custo: produto.custo,
        venda: produto.venda,
        quantidade: produto.quantidade,
        imagem_url: produto.imagem_url || "",
      });
    } else {
      setEditingProduto(null);
      form.reset({
        nome: "",
        descricao: "",
        categoria: "",
        custo: 0,
        venda: 0,
        quantidade: 0,
        imagem_url: "",
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (data) => {
    setSubmitting(true);
    try {
      if (editingProduto) {
        await authAxios.put(`/produtos/${editingProduto.id}`, data);
        toast.success("Produto atualizado!");
      } else {
        await authAxios.post("/produtos", data);
        toast.success("Produto cadastrado!");
      }
      setDialogOpen(false);
      fetchProdutos();
      fetchStats();
      fetchCategorias();
    } catch (err) {
      toast.error("Erro ao salvar produto");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este produto?")) return;
    try {
      await authAxios.delete(`/produtos/${id}`);
      toast.success("Produto excluído!");
      fetchProdutos();
      fetchStats();
      fetchCategorias();
    } catch (err) {
      toast.error("Erro ao excluir produto");
    }
  };

  const handleUpdateQuantidade = async (produto, delta) => {
    const novaQuantidade = produto.quantidade + delta;
    if (novaQuantidade < 0) {
      toast.error("Quantidade não pode ser negativa");
      return;
    }
    try {
      await authAxios.put(`/produtos/${produto.id}`, { quantidade: novaQuantidade });
      if (delta < 0) {
        toast.success(`1 unidade vendida! Restam ${novaQuantidade}`);
      } else {
        toast.success(`Estoque atualizado: ${novaQuantidade} unidades`);
      }
      fetchProdutos();
      fetchStats();
    } catch (err) {
      toast.error("Erro ao atualizar quantidade");
    }
  };

  // Funções de gerenciamento de categorias
  const handleAddCategoria = async () => {
    if (!novaCategoria.trim()) {
      toast.error("Digite o nome da categoria");
      return;
    }
    if (categorias.includes(novaCategoria.trim())) {
      toast.error("Categoria já existe");
      return;
    }
    try {
      await authAxios.post("/produtos/categorias", { nome: novaCategoria.trim() });
      toast.success("Categoria criada!");
      setNovaCategoria("");
      fetchCategorias();
    } catch (err) {
      toast.error("Erro ao criar categoria");
    }
  };

  const handleEditCategoria = async (categoriaAntiga) => {
    if (!editandoCategoriaValor.trim()) {
      toast.error("Digite o nome da categoria");
      return;
    }
    if (categoriaAntiga === editandoCategoriaValor.trim()) {
      setEditandoCategoria(null);
      return;
    }
    try {
      await authAxios.put("/produtos/categorias", { 
        categoria_antiga: categoriaAntiga, 
        categoria_nova: editandoCategoriaValor.trim() 
      });
      toast.success("Categoria atualizada!");
      setEditandoCategoria(null);
      setEditandoCategoriaValor("");
      fetchCategorias();
      fetchProdutos();
      if (categoriaFiltro === categoriaAntiga) {
        setCategoriaFiltro(editandoCategoriaValor.trim());
      }
    } catch (err) {
      toast.error("Erro ao atualizar categoria");
    }
  };

  const handleDeleteCategoria = async (categoria) => {
    if (!window.confirm(`Remover a categoria "${categoria}"? Os produtos desta categoria ficarão sem categoria.`)) return;
    try {
      await authAxios.delete(`/produtos/categorias/${encodeURIComponent(categoria)}`);
      toast.success("Categoria removida!");
      fetchCategorias();
      fetchProdutos();
      fetchStats();
      if (categoriaFiltro === categoria) {
        setCategoriaFiltro("");
      }
    } catch (err) {
      toast.error("Erro ao remover categoria");
    }
  };

  const filteredProdutos = produtos.filter(p => {
    const matchSearch = p.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.descricao?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategoria = !categoriaFiltro || p.categoria === categoriaFiltro;
    return matchSearch && matchCategoria;
  });

  // Paginação
  const totalItems = filteredProdutos.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedProdutos = filteredProdutos.slice(startIndex, endIndex);

  // Reset page when search or category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoriaFiltro]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="produtos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Produtos</h2>
          <p className="text-muted-foreground">
            Controle de estoque e cálculo de lucro
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => { fetchProdutos(); fetchStats(); toast.success("Atualizado!"); }}
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={() => handleOpenDialog()} data-testid="new-produto-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Produto
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 text-blue-500" />
                <span className="text-xs font-medium text-muted-foreground">PRODUTOS</span>
              </div>
              <p className="text-2xl font-bold">{stats.total_produtos}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Boxes className="w-4 h-4 text-purple-500" />
                <span className="text-xs font-medium text-muted-foreground">EM ESTOQUE</span>
              </div>
              <p className="text-2xl font-bold">{stats.total_itens}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <ArrowDownRight className="w-4 h-4 text-red-500" />
                <span className="text-xs font-medium text-muted-foreground">CUSTO TOTAL</span>
              </div>
              <p className="text-xl font-bold text-red-500">{formatCurrency(stats.total_custo)}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <ArrowUpRight className="w-4 h-4 text-blue-500" />
                <span className="text-xs font-medium text-muted-foreground">VENDA TOTAL</span>
              </div>
              <p className="text-xl font-bold text-blue-500">{formatCurrency(stats.total_venda)}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-emerald-500/10 border-emerald-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-medium text-emerald-500">LUCRO TOTAL</span>
              </div>
              <p className="text-xl font-bold text-emerald-500">{formatCurrency(stats.total_lucro)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar produto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-muted-foreground" />
          <Select
            value={categoriaFiltro || "todas"}
            onValueChange={(value) => setCategoriaFiltro(value === "todas" ? "" : value)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Todas categorias" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas categorias</SelectItem>
              {categorias.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {categoriaFiltro && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCategoriaFiltro("")}
              className="h-8 w-8"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCategoriasDialogOpen(true)}
            title="Gerenciar Categorias"
            data-testid="manage-categories-btn"
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats por Categoria */}
      {stats?.por_categoria && Object.keys(stats.por_categoria).length > 1 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(stats.por_categoria).map(([cat, data]) => (
            <button
              key={cat}
              onClick={() => setCategoriaFiltro(cat === "Sem categoria" ? "" : cat)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                categoriaFiltro === cat || (cat === "Sem categoria" && !categoriaFiltro)
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted hover:bg-muted/80"
              }`}
            >
              {cat} ({data.produtos})
            </button>
          ))}
        </div>
      )}

      {/* Products Grid */}
      {filteredProdutos.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nenhum produto cadastrado</p>
            <p className="text-sm mt-2">Clique em "Novo Produto" para adicionar</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {paginatedProdutos.map((produto) => (
              <Card key={produto.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                {produto.imagem_url && (
                  <div className="h-40 bg-muted">
                    <img 
                      src={produto.imagem_url} 
                      alt={produto.nome}
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.parentElement.style.display = 'none'}
                    />
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-bold truncate flex-1">{produto.nome}</h3>
                    {produto.categoria && (
                      <Badge variant="secondary" className="text-xs shrink-0">
                        {produto.categoria}
                      </Badge>
                    )}
                  </div>
                  
                  {produto.descricao && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {produto.descricao}
                    </p>
                  )}
                  
                  {/* Controle de Quantidade */}
                  <div className="flex items-center justify-between p-3 bg-purple-500/10 rounded-lg border border-purple-500/30 mb-3">
                    <div>
                      <p className="text-xs text-purple-500 font-medium">ESTOQUE</p>
                      <p className="text-lg font-bold text-purple-500">{produto.quantidade} un</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                        onClick={() => handleUpdateQuantidade(produto, -1)}
                        disabled={produto.quantidade <= 0}
                        title="Vender 1 unidade"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 border-green-500 text-green-500 hover:bg-green-500 hover:text-white"
                        onClick={() => handleUpdateQuantidade(produto, 1)}
                        title="Adicionar 1 unidade"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="p-2 bg-red-500/10 rounded-lg">
                      <p className="text-xs text-red-500 font-medium">CUSTO</p>
                      <p className="text-sm font-bold text-red-500">{formatCurrency(produto.custo)}</p>
                    </div>
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <p className="text-xs text-blue-500 font-medium">VENDA</p>
                      <p className="text-sm font-bold text-blue-500">{formatCurrency(produto.venda)}</p>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30 mb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-emerald-500 font-medium">LUCRO</p>
                        <p className="text-lg font-bold text-emerald-500">{formatCurrency(produto.lucro)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Margem</p>
                        <p className="text-lg font-bold text-emerald-500">{produto.margem}%</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleOpenDialog(produto)}
                    >
                      <Pencil className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(produto.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Itens por página:</span>
                <Select
                  value={String(itemsPerPage)}
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground ml-4">
                  Mostrando {startIndex + 1}-{endIndex} de {totalItems}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Anterior
                </Button>
                <span className="text-sm px-4">
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingProduto ? "Editar Produto" : "Novo Produto"}
            </DialogTitle>
            <DialogDescription>
              Preencha as informações do produto
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome do Produto *</Label>
                <Input
                  id="nome"
                  placeholder="Nome do produto"
                  {...form.register("nome")}
                />
                {form.formState.errors.nome && (
                  <p className="text-sm text-destructive">{form.formState.errors.nome.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  placeholder="Descrição do produto"
                  {...form.register("descricao")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="categoria">Categoria</Label>
                <Select
                  value={form.watch("categoria") || "sem-categoria"}
                  onValueChange={(value) => form.setValue("categoria", value === "sem-categoria" ? "" : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sem-categoria">Sem categoria</SelectItem>
                    {CATEGORIAS_SUGERIDAS.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                    {categorias
                      .filter(c => !CATEGORIAS_SUGERIDAS.includes(c))
                      .map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Ou digite uma nova categoria:
                </p>
                <Input
                  placeholder="Nova categoria personalizada"
                  value={form.watch("categoria") || ""}
                  onChange={(e) => form.setValue("categoria", e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="custo">Custo (R$)</Label>
                  <Input
                    id="custo"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    {...form.register("custo")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="venda">Venda (R$)</Label>
                  <Input
                    id="venda"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    {...form.register("venda")}
                  />
                </div>
              </div>

              {/* Preview do Lucro */}
              <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-emerald-500 font-medium">LUCRO CALCULADO</p>
                    <p className="text-2xl font-bold text-emerald-500">{formatCurrency(lucroPreview)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Margem de Lucro</p>
                    <p className="text-2xl font-bold text-emerald-500">{margemPreview}%</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="quantidade">Quantidade em Estoque</Label>
                <Input
                  id="quantidade"
                  type="number"
                  placeholder="0"
                  {...form.register("quantidade")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="imagem_url">URL da Imagem</Label>
                <Input
                  id="imagem_url"
                  placeholder="https://exemplo.com/imagem.png"
                  {...form.register("imagem_url")}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Salvando..." : editingProduto ? "Atualizar" : "Cadastrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Gerenciar Categorias */}
      <Dialog open={categoriasDialogOpen} onOpenChange={setCategoriasDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Gerenciar Categorias
            </DialogTitle>
            <DialogDescription>
              Adicione, edite ou remova categorias dos seus produtos
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Adicionar nova categoria */}
            <div className="flex gap-2">
              <Input
                placeholder="Nova categoria..."
                value={novaCategoria}
                onChange={(e) => setNovaCategoria(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddCategoria()}
              />
              <Button onClick={handleAddCategoria} size="icon">
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Lista de categorias */}
            <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
              {categorias.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  <Tag className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhuma categoria criada</p>
                </div>
              ) : (
                categorias.map((cat) => (
                  <div key={cat} className="flex items-center justify-between p-3 hover:bg-muted/50">
                    {editandoCategoria === cat ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          value={editandoCategoriaValor}
                          onChange={(e) => setEditandoCategoriaValor(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleEditCategoria(cat)}
                          className="h-8"
                          autoFocus
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-green-500 hover:text-green-600"
                          onClick={() => handleEditCategoria(cat)}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            setEditandoCategoria(null);
                            setEditandoCategoriaValor("");
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{cat}</Badge>
                          <span className="text-xs text-muted-foreground">
                            ({stats?.por_categoria?.[cat]?.produtos || 0} produtos)
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              setEditandoCategoria(cat);
                              setEditandoCategoriaValor(cat);
                            }}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleDeleteCategoria(cat)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Categorias sugeridas */}
            {CATEGORIAS_SUGERIDAS.filter(c => !categorias.includes(c)).length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Sugestões:</p>
                <div className="flex flex-wrap gap-1">
                  {CATEGORIAS_SUGERIDAS.filter(c => !categorias.includes(c)).map((sug) => (
                    <button
                      key={sug}
                      onClick={() => setNovaCategoria(sug)}
                      className="px-2 py-1 text-xs bg-muted rounded hover:bg-muted/80 transition-colors"
                    >
                      + {sug}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoriasDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
